/**
 * Single source of truth for reading prices on the shop frontend.
 *
 * The backend (`getDisplayPrice`) attaches a structured `price` object to every
 * article it returns:
 *
 *   { currentPrice, basePrice, originalPrice, inDiscount, discountPercentage, currency }
 *
 * This helper normalizes anything the frontend might receive (article object,
 * full product, raw search scalar, legacy shapes, cart item) into that same
 * canonical shape so display code never branches.
 */

const EMPTY = Object.freeze({
  currentPrice: null,
  basePrice: null,
  inDiscount: false,
  discountPercentage: null,
  currency: 'CHF',
});

const normalize = (p) => ({
  currentPrice: p?.currentPrice ?? null,
  basePrice: p?.basePrice ?? p?.originalPrice ?? null,
  inDiscount: !!p?.inDiscount,
  discountPercentage: p?.discountPercentage ?? null,
  currency: p?.currency || 'CHF',
});

const isStructuredPrice = (v) =>
  v && typeof v === 'object' && ('currentPrice' in v || 'basePrice' in v || 'originalPrice' in v);

/**
 * Accepts an article, a product, a price object, a cart item, or a scalar number.
 * Always returns the canonical shape — never null.
 *
 * Precedence note (important): if the input has a structured `price` field,
 * we ALWAYS use that. Some backend endpoints return articles with legacy
 * top-level mirror fields (`basePrice`, `inDiscount`, `discountPrice`) that
 * would otherwise make `isStructuredPrice(input)` true and shadow the
 * actual nested `price` object — leaving `currentPrice` null and breaking
 * discount rendering. Always prefer the dedicated `price` field.
 */
export const getPriceShape = (input) => {
  if (input == null) return { ...EMPTY };

  // Highest priority: a dedicated structured `price` field on the input.
  if (isStructuredPrice(input.price)) return normalize(input.price);

  // Product whose first article has a structured `price`.
  const firstArticle = Array.isArray(input.articles) ? input.articles[0] : null;
  if (isStructuredPrice(firstArticle?.price)) return normalize(firstArticle.price);

  // Input IS a structured price object itself (no nested `.price` field).
  // Only treat as such when there's no nested price candidate above — this
  // is the right fallback for raw price objects returned by some helpers,
  // but must not shadow the nested case (see precedence note above).
  if (isStructuredPrice(input)) return normalize(input);

  // Scalar `price` on the input itself (legacy, search, cart line)
  if (typeof input.price === 'number') {
    return { ...EMPTY, currentPrice: input.price, basePrice: input.price };
  }
  if (typeof input.price === 'string' && input.price && !isNaN(input.price)) {
    const n = Number(input.price);
    return { ...EMPTY, currentPrice: n, basePrice: n };
  }

  // Scalar on the first article
  if (firstArticle && typeof firstArticle.price === 'number') {
    return { ...EMPTY, currentPrice: firstArticle.price, basePrice: firstArticle.price };
  }

  // Legacy `prices` field as a single value (server search, bestseller endpoint)
  if (typeof input.prices === 'number' || (typeof input.prices === 'string' && !isNaN(input.prices))) {
    const n = Number(input.prices);
    return { ...EMPTY, currentPrice: n, basePrice: n };
  }

  // Scalar input
  if (typeof input === 'number') {
    return { ...EMPTY, currentPrice: input, basePrice: input };
  }

  return { ...EMPTY };
};

/**
 * Get the numeric current price for arithmetic (cart totals, etc.).
 * Returns 0 when no price is known so cart math doesn't NaN.
 */
export const getNumericPrice = (input) => {
  const p = getPriceShape(input);
  return p.currentPrice != null ? Number(p.currentPrice) : 0;
};

export const formatCHF = (n, locale = 'de-CH') =>
  Number(n).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const PRICE_EMPTY = EMPTY;
